/** A rich-text format stated as data. Library-specific adapters consume this. */
export type RichTextProfile = {
  nodes: readonly (
    | 'paragraph'
    | 'heading'
    | 'bulletList'
    | 'orderedList'
    | 'divider'
  )[]
  marks: readonly ('bold' | 'italic' | 'underline' | 'link')[]
  headingLevels: readonly (1 | 2 | 3)[]
  alignments: readonly ('left' | 'center' | 'right')[]
  linkSchemes: readonly string[]
  chips: boolean
  tokenFamilies: readonly ('message' | 'schedule' | 'campaign' | 'noticeTime')[]
}

const authored = {
  nodes: ['paragraph', 'heading', 'bulletList', 'orderedList', 'divider'],
  marks: ['bold', 'italic', 'underline', 'link'],
  headingLevels: [1, 2, 3],
  alignments: ['left', 'center', 'right'],
  linkSchemes: ['http', 'https', 'mailto', 'tel'],
} as const

export const RICH_TEXT_PROFILES = {
  email: {
    ...authored,
    chips: true,
    tokenFamilies: ['message', 'schedule', 'campaign'],
  },
  notice: {
    ...authored,
    chips: false,
    tokenFamilies: ['noticeTime'],
  },
} as const satisfies Record<string, RichTextProfile>

export type RichTextProfileId = keyof typeof RICH_TEXT_PROFILES

/** The sanitizer shape derived from the same profile the editor reads. */
export function richTextAllowlist(profile: RichTextProfile) {
  const tags = new Set<string>()
  if (profile.nodes.includes('paragraph')) tags.add('p')
  if (profile.nodes.includes('heading'))
    for (const level of profile.headingLevels) tags.add(`h${level}`)
  if (profile.nodes.includes('bulletList')) tags.add('ul')
  if (profile.nodes.includes('orderedList')) tags.add('ol')
  if (
    profile.nodes.includes('bulletList') ||
    profile.nodes.includes('orderedList')
  )
    tags.add('li')
  if (profile.nodes.includes('divider')) tags.add('hr')
  tags.add('br')
  if (profile.marks.includes('bold')) tags.add('strong')
  if (profile.marks.includes('italic')) tags.add('em')
  if (profile.marks.includes('underline')) tags.add('u')
  if (profile.marks.includes('link')) tags.add('a')
  return {
    tags: [...tags],
    attributes: {
      '*': ['dir', 'style'],
      a: ['href', ...(profile.chips ? ['data-chip'] : [])],
      ol: ['start'],
    } as Record<string, string[]>,
    styles: {
      '*': {
        'text-align': [new RegExp(`^(${profile.alignments.join('|')})$`)],
      },
    },
    schemes: [...profile.linkSchemes],
  }
}

type HtmlNode = {
  tag: string
  attrs: Record<string, string>
  children: HtmlNode[]
  text: string
}

const entity = (text: string) =>
  text.replace(/&(?:#\d+|#x[\da-f]+|\w+);/gi, (value) => {
    const named: Record<string, string> = {
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#39;': "'",
      '&nbsp;': ' ',
    }
    if (named[value]) return named[value]
    const hex = value.match(/^&#x([\da-f]+);$/i)
    const dec = value.match(/^&#(\d+);$/)
    const point = hex ? Number.parseInt(hex[1], 16) : dec ? Number(dec[1]) : NaN
    return Number.isFinite(point) ? String.fromCodePoint(point) : value
  })

/** Plain delivery text derived from stored HTML, on browser and server alike. */
export function richTextPlainText(html: string): string {
  const root: HtmlNode = { tag: 'root', attrs: {}, children: [], text: '' }
  const stack = [root]
  const tokens = html.match(/<!--[\s\S]*?-->|<[^>]*>|[^<]+/g) ?? []
  for (const token of tokens) {
    if (token.startsWith('<!--')) continue
    const close = token.match(/^<\s*\/\s*([\w-]+)/)
    if (close) {
      const at = stack.map((n) => n.tag).lastIndexOf(close[1].toLowerCase())
      if (at > 0) stack.length = at
      continue
    }
    const open = token.match(/^<\s*([\w-]+)([^>]*)>/)
    if (open) {
      const attrs: Record<string, string> = {}
      for (const match of open[2].matchAll(
        /([\w-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g,
      ))
        attrs[match[1].toLowerCase()] = entity(
          match[2] ?? match[3] ?? match[4] ?? '',
        )
      const node: HtmlNode = {
        tag: open[1].toLowerCase(),
        attrs,
        children: [],
        text: '',
      }
      stack.at(-1)?.children.push(node)
      if (
        !/^(br|hr|img|meta|link|input)$/i.test(node.tag) &&
        !/\/\s*>$/.test(token)
      )
        stack.push(node)
      continue
    }
    stack.at(-1)?.children.push({
      tag: '#text',
      attrs: {},
      children: [],
      text: entity(token),
    })
  }

  const inline = (node: HtmlNode): string => {
    if (node.tag === '#text') return node.text
    if (node.tag === 'br') return '\n'
    if (['script', 'style', 'template'].includes(node.tag)) return ''
    const words = node.children.map(inline).join('')
    if (node.tag !== 'a') return words
    const href = node.attrs.href
    if (!href || !safeLink(href) || saysAddress(words, href)) return words
    return `${words}: ${href}`
  }
  const blocks = (node: HtmlNode, depth = 0): string[] => {
    if (node.tag === 'ul' || node.tag === 'ol') {
      let number = node.tag === 'ol' ? Number(node.attrs.start || 1) : 0
      const lines = node.children
        .filter((n) => n.tag === 'li')
        .flatMap((item) => {
          const nested = item.children.filter(
            (n) => n.tag === 'ul' || n.tag === 'ol',
          )
          const own = item.children
            .filter((n) => n.tag !== 'ul' && n.tag !== 'ol')
            .map(inline)
            .join('')
            .trim()
          const mark = node.tag === 'ol' ? `${number++}. ` : '- '
          return [
            `${'  '.repeat(depth)}${mark}${own}`,
            ...nested.flatMap((n) =>
              blocks(n, depth + 1).flatMap((part) => part.split('\n')),
            ),
          ]
        })
      return [lines.join('\n')]
    }
    if (['p', 'h1', 'h2', 'h3'].includes(node.tag)) return [inline(node).trim()]
    if (node.tag === 'hr') return []
    if (node.tag === 'root') {
      const parts: string[] = []
      let run = ''
      for (const child of node.children) {
        if (['p', 'h1', 'h2', 'h3', 'ul', 'ol', 'hr'].includes(child.tag)) {
          if (run.trim()) parts.push(run.trim())
          run = ''
          parts.push(...blocks(child, depth))
        } else {
          run += inline(child)
        }
      }
      if (run.trim()) parts.push(run.trim())
      return parts
    }
    return node.children.flatMap((child) => blocks(child, depth))
  }
  return blocks(root)
    .filter(Boolean)
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function safeLink(href: string): boolean {
  const scheme = href.match(/^([a-z][a-z\d+.-]*):/i)?.[1]?.toLowerCase()
  return !scheme || ['http', 'https', 'mailto', 'tel'].includes(scheme)
}

/** Plain lines as authored rich text, for callers that begin without HTML. */
export function plainTextToRichText(text: string): string {
  const escaped = text.replace(
    /[&<>]/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[character] ?? character,
  )
  return escaped
    .split('\n')
    .map((line) => (line.trim() ? `<p>${line}</p>` : '<p><br></p>'))
    .join('')
}

function saysAddress(text: string, href: string): boolean {
  const bare = (value: string) =>
    value
      .replace(/^https?:\/\//, '')
      .replace(/^(mailto:|tel:)/, '')
      .replace(/\/$/, '')
  return bare(text) === bare(href)
}
